import React, { useState, useContext } from 'react';
import { ConfigContext } from '../context/ConfigContext';
import { useAuthContext } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Heart, Mail, Lock, Eye, EyeOff, ArrowRight, AlertCircle } from 'lucide-react';
import { getApiUrl } from '../utils/apiConfig';
import { 
  isValidEmail, 
  isValidPassword, 
  sanitizeString, 
  errorMessages 
} from '../utils/validation';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const { config } = useContext(ConfigContext);
  const { login } = useAuthContext();
  const { success, error: showError } = useNotification();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Validación en tiempo real
  const validateField = (name, value) => {
    let fieldError = '';
    
    if (name === 'email') {
      if (!value.trim()) {
        fieldError = errorMessages.required;
      } else if (!isValidEmail(value)) {
        fieldError = errorMessages.email;
      }
    }
    
    if (name === 'password') {
      if (!value) {
        fieldError = errorMessages.required;
      } else if (!isValidPassword(value)) {
        fieldError = errorMessages.password;
      }
    }
    
    setErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));
    
    return !fieldError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar todos los campos antes de enviar
    const emailValid = validateField('email', formData.email);
    const passwordValid = validateField('password', formData.password);
    
    if (!emailValid || !passwordValid) {
      showError('Por favor, corrija los errores en el formulario');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Sanitizar datos antes de enviar
      const sanitizedData = {
        email: sanitizeString(formData.email.trim().toLowerCase()),
        password: formData.password // No sanitizar password ya que puede tener caracteres especiales
      };
      
      const response = await fetch(getApiUrl('/api/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sanitizedData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        success('Bienvenido de nuevo', 'Inicio de sesión exitoso');
        login(data.user);
        setTimeout(() => navigate('/dashboard'), 500);
      } else {
        // Manejar diferentes códigos de error
        switch (response.status) {
          case 400:
            showError(data.error || 'Datos incompletos');
            break;
          case 401:
            showError('Credenciales incorrectas', 'Verifique su correo y contraseña');
            break;
          case 403:
            if (data.error.includes('pendiente')) {
              showError('Cuenta pendiente de aprobación', 'Contacte al administrador');
            } else if (data.error.includes('expirado')) {
              showError('Cuenta expirada', 'Contacte al administrador para renovar');
            } else {
              showError('Acceso denegado', data.error);
            }
            break;
          case 500:
            showError('Error del servidor', 'Intente nuevamente en unos momentos');
            break;
          default:
            showError(data.error || errorMessages.invalidCredentials);
        }
      }
    } catch (err) {
      console.error('Error de login:', err);
      showError(errorMessages.networkError, 'No se pudo conectar con el servidor');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo mientras el usuario escribe
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <motion.div 
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl p-8 shadow-xl">
          <motion.div 
            className="text-center mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {/* Logo dinámico */}
            {config.loginLogo ? (
              <img src={config.loginLogo} alt="Logo" className="w-16 h-16 rounded-2xl mx-auto mb-4 object-contain" />
            ) : (
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-white" />
              </div>
            )}
            {/* Título dinámico */}
            <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
              {config.organizationName || 'BeneficiaHub'}
            </h1>
            {/* Subtítulo dinámico */}
            <p className="text-gray-500 font-medium">{config.loginSubtitle || 'Sistema de Gestión de Beneficencia'}</p>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correo Electrónico
              </label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                  errors.email ? 'text-red-400' : 'text-gray-400'
                }`} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="ejemplo@correo.com"
                  className={`w-full pl-10 pr-4 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${
                    errors.email 
                      ? 'border-red-300 focus:ring-red-500/30 focus:border-red-500' 
                      : 'border-gray-200 focus:ring-blue-500/30 focus:border-blue-500'
                  }`}
                  disabled={isLoading}
                  autoComplete="email"
                />
                {errors.email && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                )}
              </div>
              {errors.email && (
                <motion.p 
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-1 text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  {errors.email}
                </motion.p>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                  errors.password ? 'text-red-400' : 'text-gray-400'
                }`} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="••••••••"
                  className={`w-full pl-10 pr-12 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${
                    errors.password 
                      ? 'border-red-300 focus:ring-red-500/30 focus:border-red-500' 
                      : 'border-gray-200 focus:ring-blue-500/30 focus:border-blue-500'
                  }`}
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <motion.p 
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-1 text-sm text-red-600 flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  {errors.password}
                </motion.p>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center justify-between"
            >
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="ml-2 text-sm text-gray-600">Recordarme</span>
              </label>
              <Link 
                to="/forgot-password" 
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                ¿Olvidó su contraseña?
              </Link>
            </motion.div>

            <motion.button
              type="submit"
              disabled={isLoading || Object.keys(errors).some(key => errors[key])}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={!isLoading ? { scale: 1.02 } : {}}
              whileTap={!isLoading ? { scale: 0.98 } : {}}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Verificando...
                </>
              ) : (
                <>
                  Iniciar Sesión
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </motion.button>
          </form>

          <motion.div 
            className="mt-6 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <p className="text-gray-600">
              ¿No tiene una cuenta?{' '}
              <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
                Regístrese ahora
              </Link>
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;